/* eslint-disable react/no-unescaped-entities */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from 'prop-types';

import { useCSChatBot } from '@zenbusiness/application-commons-cs-chatbot';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import Box from '@zenbusiness/zui/Box';
import Link from '@zenbusiness/zui/Link';
import Text from '@zenbusiness/zui/Text';

import {
  SupportCardRoot,
  SupportCardSectionTitle,
  SupportCardDescription,
  SupportCardButton,
  SupportCardSection
} from './SupportCard.style';
import { CLICKSTREAM_COMMON_PROPS } from '../utils/constants';

const supportData = {
  email: {
    title: 'Send an Email',
    description: (
      <>
        <p>Cut and paste the summary above into the chat window and I'll send it to them.</p>
        <p>We usually reply within 1 to 2 business days of receiving your email.</p>
      </>
    ),
    buttonText: 'Open Email',
    buttonIcon: <OpenInNewIcon size={16} />,
    link: 'mailto:support@zenbusiness.com'
  },
  call: {
    title: 'Call Support',
    description: (
      <>
        Need to talk it through? You can reach our Customer Support team directly at{' '}
        <Link
          clickstreamProps={{
            ...CLICKSTREAM_COMMON_PROPS,
            elementName: 'call_support'
          }}
          href="tel:8444936249"
        >
          844-493-6249
        </Link>
        .
      </>
    )
  },
  chat: {
    title: 'Chat with an Expert',
    description:
      "Great, I'll connect you with a person who can help. Cut and paste the summary above into the chat window and I'll send it to them.",
    buttonText: 'Chat now',
    // buttonIcon: <CopyAllIcon size={16} />,
    onClick: () =>
      navigator.clipboard.writeText('Hello, I would like to talk to a human about my account.')
  }
};

const SupportTime = ({ action = 'Call' }) => {
  return (
    <Box display="flex" flexDirection="column" gap={1} mt={2}>
      <Text fontWeight="600" size="sm">
        {action} during our business hours:
      </Text>
      <Text size="sm">Mon to Fri – 8am to 8pm CT | Sat/Sun – 9am to 6pm CT</Text>
    </Box>
  );
};

SupportTime.propTypes = {
  action: PropTypes.string
};

const SupportCard = () => {
  const { name, email, createdAt, userUuid } = useIdentity();
  const {
    update: updateCSChatBot,
    toggle: toggleCSChatBot,
    setFirstAutomatedMessageContent
  } = useCSChatBot();

  const handleOpenIntercom = () => {
    updateCSChatBot({
      user_id: userUuid,
      email,
      name,
      creationDate: createdAt
    });
    setFirstAutomatedMessageContent('Hello, I would like to talk to a human about my account.');
    toggleCSChatBot(true);
  };

  return (
    <SupportCardRoot>
      {/* Chat Section */}
      <SupportCardSection>
        <SupportCardSectionTitle fontWeight="600">{supportData.chat.title}</SupportCardSectionTitle>
        <SupportCardDescription>{supportData.chat.description}</SupportCardDescription>
        <SupportTime action="Chat" />
        <SupportCardButton
          clickstreamProps={{
            ...CLICKSTREAM_COMMON_PROPS,
            elementName: 'chat_now'
          }}
          endIcon={supportData.chat.buttonIcon}
          variant="outlined"
          onClick={handleOpenIntercom}
        >
          {supportData.chat.buttonText}
        </SupportCardButton>
      </SupportCardSection>
      {/* Call Section */}
      <SupportCardSection>
        <SupportCardSectionTitle fontWeight="600">{supportData.call.title}</SupportCardSectionTitle>
        <SupportCardDescription>{supportData.call.description}</SupportCardDescription>
        <SupportTime action="Call" />
      </SupportCardSection>
      {/* Email Section */}
      <SupportCardSection>
        <SupportCardSectionTitle fontWeight="600">
          {supportData.email.title}
        </SupportCardSectionTitle>
        <SupportCardDescription>{supportData.email.description}</SupportCardDescription>
        <SupportCardButton
          clickstreamProps={{
            ...CLICKSTREAM_COMMON_PROPS,
            elementName: 'open_email'
          }}
          href={supportData.email.link}
          icon={supportData.email.buttonIcon}
          variant="outlined"
        >
          {supportData.email.buttonText}
        </SupportCardButton>
      </SupportCardSection>
    </SupportCardRoot>
  );
};

export default SupportCard;
